import { roundOff } from "src/assets/scripts/utilities/helpers";

export function useProductList({
  formData,
  purchaseOrder,
  excludedCategories,
}) {
  const pagination = computed({
    get() {
      return {
        rowsPerPage: 25,
      };
    },
  });

  const updateProductRate = (product, index) => {
    let isTaxIncluded = JSON.parse(product?.is_tax_included ?? false);
    let rate = isTaxIncluded ? Number(product.rate_wt) : Number(product?.rate);
    let gst = Number(product?.gst) || 0;
    let cess = Number(product?.cess) || 0;

    if (isTaxIncluded) {
      unref(formData).lineitems[index].rate = rate / (1 + (gst + cess) / 100);
    } else {
      unref(formData).lineitems[index].rate_wt =
        rate + rate * ((gst + cess) / 100);
    }

    updateProductAmount(unref(formData).lineitems[index], index);
  };

  const updateProductAmount = (product, index) => {
    unref(formData).lineitems[index].amount =
      Number(product?.qty) * Number(product?.rate);

    unref(formData).lineitems[index].amount_wt =
      Number(product?.qty) * Number(product?.rate_wt);

    calculateTotalTax();
  };

  const calculateTotalTax = () => {
    unref(formData).total_tax = unref(formData).lineitems.reduce(
      (total, product) => {
        let gst = Number(product?.gst) || 0;
        let cess = Number(product?.cess) || 0;

        total +=
          Number(product?.qty) * Number(product?.rate) * ((gst + cess) / 100);

        return total;
      },
      0,
    );

    calculateTotalAmount();
  };

  const getTotalAmountWithoutTax = () => {
    return unref(formData)?.lineitems?.reduce((total, product) => {
      total += Number(product?.amount);

      return total;
    }, 0);
  };

  const calculateTotalAmount = () => {
    unref(formData).total_amount = unref(formData).lineitems.reduce(
      (total, product) => {
        total += Number(product?.amount_wt);

        return total;
      },
      0,
    );

    if (
      unref(formData).seller?.tax_settings?._id != null ||
      unref(formData).tds?.code != null
    ) {
      calculateTds();
    }

    if (unref(purchaseOrder)?.buyer?.tax_settings?._id != null) {
      calculateTcs(unref(formData).total_amount);
    }

    if (!unref(formData).is_freight_included) {
      calculateFreight();
    }
  };

  const calculateFreight = () => {
    unref(formData).freight = unref(formData).lineitems.reduce(
      (total, product) => {
        if (product.name.includes("Freight")) {
          total += Number(product?.amount_wt);
        }

        return total;
      },
      0,
    );
  };

  const calculateTds = () => {
    if (
      unref(formData).seller?.tax_settings?._id != null ||
      unref(formData).tds?.code != null
    ) {
      const tdsPercentage =
        unref(formData).seller?.tax_settings?.percentage ??
        unref(formData).tds?.percentage;
      const tdsCode =
        unref(formData).seller?.tax_settings?.code ?? unref(formData).tds?.code;
      const totalAmount = getTotalAmountWithoutTax();
      const tdsAmount = (tdsPercentage / 100) * totalAmount;

      unref(formData).tds = {
        code: tdsCode,
        percentage: tdsPercentage,
        amount: tdsAmount ?? 0,
      };
    } else {
      unref(formData).tds = null;
    }
  };

  const calculateTcs = (totalAmount) => {
    if (unref(purchaseOrder)?.buyer?.tax_settings?._id != null) {
      const tcsPercentage = unref(purchaseOrder).buyer.tax_settings.percentage;
      const tcsCode = unref(purchaseOrder).buyer.tax_settings.code;
      const tcsAmount = (tcsPercentage / 100) * totalAmount;

      unref(formData).tcs = {
        code: tcsCode,
        percentage: tcsPercentage,
        amount: tcsAmount ?? 0,
      };
    } else {
      unref(formData).tcs = null;
    }
  };

  const onAddItem = (item) => {
    unref(formData).lineitems.push(item);

    nextTick(() => {
      calculateTotalTax();
    });
  };

  const onEditItem = (item, index) => {
    unref(formData).lineitems[index] = item;

    nextTick(() => {
      calculateTotalTax();
    });
  };

  const { toggleItemForm } = usePurchaseOrderStore();

  const addItem = (props) => {
    toggleItemForm({
      open: true,
      props: {
        ...props,
        entity: { ...unref(formData), type: props.entityType },
        items: props.items,
        toggle: { method: onAddItem },
      },
    });
  };

  const editItem = (props) => {
    toggleItemForm({
      open: true,
      edit: true,
      props: {
        ...props,
        entity: { ...unref(formData), type: props.entityType },
        toggle: {
          method: onEditItem,
        },
      },
    });
  };

  const removeProduct = (index) => {
    unref(formData).lineitems.splice(index, 1);

    calculateTotalTax();
  };

  const marginTypes = ["Negative", "Zero", "Below Expectations"];

  const getConvertedQty = (lineItem) => {
    const { is_po_unit_bigger, conversion_factor, pb_qty, qty } = lineItem;

    if (is_po_unit_bigger) {
      return Number(pb_qty ?? qty) / Number(conversion_factor);
    }

    return Number(pb_qty ?? qty) * Number(conversion_factor);
  };

  const getConvertedRate = (product) => {
    const { is_po_unit_bigger, conversion_factor, pb_rate } = product;

    if (is_po_unit_bigger) {
      return Number(pb_rate) * Number(conversion_factor);
    }

    return Number(pb_rate) / Number(conversion_factor);
  };

  const getConvertedRateWithTax = (product) => {
    const { pb_gst, pb_cess = 0 } = product;

    return getConvertedRate(product) * (1 + (pb_gst + pb_cess) / 100);
  };

  const getGstLiability = (product) => {
    const { pb_gst, pb_cess = 0, rate, rate_wt } = product;

    const pbRateWithTax =
      getConvertedRate(product) * (1 + (pb_gst + pb_cess) / 100);

    const pbTax = pbRateWithTax - getConvertedRate(product);
    const tax = rate_wt - rate;

    return tax - pbTax;
  };

  const getSalesMargin = (product) => {
    const { rate_wt } = product;

    return (
      roundOff(
        ((rate_wt -
          getConvertedRateWithTax(product) -
          getGstLiability(product)) /
          rate_wt) *
          100,
      ) || 0
    );
  };

  const getMarginClasses = (product) => {
    const { margin_expectation } = product;

    const minMarginExpectation = margin_expectation?.min ?? 0.3;

    const salesMargin = getSalesMargin(product);

    return {
      "bg-error-20": salesMargin < 0,
      "bg-warning-20": salesMargin === 0,
      "bg-success-20": salesMargin > 0 && salesMargin < minMarginExpectation,
    };
  };

  const overallMargin = computed(() => {
    const { lineitems } = unref(formData);

    const filteredLineItems = lineitems.filter(
      (lineItem) => !unref(excludedCategories)?.has(lineItem.category_id),
    );

    const excludedTotal = lineitems
      ?.filter((lineItem) =>
        unref(excludedCategories)?.has(lineItem.category_id),
      )
      ?.reduce((total, lineItem) => {
        const { amount_wt } = lineItem;

        total += amount_wt;

        return total;
      }, 0);

    if (!filteredLineItems?.length) {
      return {
        expected: 0,
        sales: 0,
      };
    }

    const totalMarginAmount = filteredLineItems.reduce((total, lineItem) => {
      const { amount_wt } = lineItem;

      total += amount_wt * getSalesMargin(lineItem);

      return total;
    }, 0);

    const totalAmount =
      roundOff(unref(formData).total_amount - excludedTotal) || 1;

    return {
      expected:
        roundOff(unref(purchaseOrder)?.buyer?.min_net_sales_margin) || 0,
      sales: roundOff(totalMarginAmount / totalAmount) || 0,
    };
  });

  const overallMarginClasses = computed(() => {
    if (!unref(formData).lineitems.length) {
      return "bg-neutral-10 q-pa-xxs";
    }

    if (unref(overallMargin).sales < 0) {
      return "bg-error-20 q-pa-xxs";
    }

    if (unref(overallMargin).sales === 0) {
      return "bg-warning-20 q-pa-xxs";
    }

    if (unref(overallMargin).sales > 0 && unref(overallMargin).sales < 0.3) {
      return "bg-success-20 q-pa-xxs";
    }

    return "bg-neutral-10 q-pa-xxs";
  });

  const hasGstExemptedItem = (lineItems) => {
    return lineItems.some((item) => item.gst_exempted);
  };

  const hasGstExemptedColumn = (columns) => {
    return columns.some((col) => col.name === "gst_exempted");
  };

  const hasGstAndCessColumn = (columns) => {
    return (
      columns.some((col) => col.name === "gst") &&
      columns.some((col) => col.name === "cess")
    );
  };

  const updateColumns = (
    initialColumns,
    gstFieldDisable = false,
    cessFieldDisable = false,
  ) => {
    if (hasGstExemptedItem(unref(formData).lineitems)) {
      if (!hasGstExemptedColumn(initialColumns)) {
        initialColumns.splice(2, hasGstAndCessColumn(initialColumns) ? 2 : 0, {
          align: "left",
          name: "gst_exempted",
          label: "GST Exempted",
          field: "gst_exempted",
        });
      }
    } else if (!hasGstAndCessColumn(initialColumns)) {
      initialColumns.splice(
        2,
        hasGstExemptedColumn(initialColumns) ? 1 : 0,
        {
          align: "left",
          name: "gst",
          label: "GST (%)*",
          field: "gst",
          disable: gstFieldDisable,
        },
        {
          align: "left",
          name: "cess",
          label: "CESS (%)*",
          field: "cess",
          disable: cessFieldDisable,
        },
      );
    }

    return initialColumns;
  };

  const hasItemsWithZeroQtyOrRate = computed(() => {
    if (!unref(formData)?.lineitems?.length) {
      return false;
    }

    return unref(formData).lineitems.some((item) => !(item.qty && item.rate));
  });

  return {
    pagination,
    marginTypes,
    overallMargin,
    overallMarginClasses,
    hasItemsWithZeroQtyOrRate,

    addItem,
    editItem,
    removeProduct,

    calculateTotalAmount,
    calculateTotalTax,
    updateProductRate,

    getConvertedQty,
    getConvertedRate,
    getTotalAmountWithoutTax,

    getSalesMargin,
    getMarginClasses,

    calculateTds,
    calculateTcs,
    updateColumns,
  };
}
